/**
 * <filter-drawer> — the Sort & filter slide-out on snippets/filter-drawer.liquid,
 * shared by sections/collection.liquid and sections/search.liquid.
 *
 * WHY THIS IS A STANDALONE ASSET AND NOT A {% javascript %} BLOCK.
 *
 * Shopify compiles a section's {% javascript %} into one bundle gated on
 * `if (__sections__.<type>)` inside a single shared try/catch — and in this
 * theme that layer has already silently failed to define <mega-menu>, leaving
 * the element in the DOM but never upgraded, with no error anywhere. Same
 * precedent as cart-drawer.js, mega-menu.js, predictive-search.js, load-more.js.
 *
 * Loaded by the two sections rather than by the layout, like load-more.js: it
 * is needed on two templates, not on every page.
 *
 * ---------------------------------------------------------------------------
 * THREE THINGS HERE ARE STRUCTURAL, NOT INCIDENTAL
 * ---------------------------------------------------------------------------
 *
 * 1. IT RELOCATES ITSELF TO <body> ON CONNECT. The drawer is rendered from a
 *    section, so it starts inside #PageContent — the layout wrapper that takes
 *    a CSS translate when the site credits bar opens. A transform makes its
 *    element a containing block for every `position: fixed` descendant, so left
 *    in place this drawer would stop being viewport-fixed and would size itself
 *    against the whole page the instant credits opened. <site-credits> solves
 *    the identical problem the identical way; see assets/footer.js.
 *
 *    The move re-fires connectedCallback, so the relocation branch returns
 *    early and the second pass does the real setup. It removes any stale copy
 *    by id first, which is what makes a theme-editor re-render (fresh drawer
 *    rendered inside the section, old one already parked on <body>) idempotent
 *    rather than leaving two stacked on each other.
 *
 * 2. THE SECTION ID ARRIVES AS AN ATTRIBUTE. cart-drawer.js derives its id from
 *    the enclosing `.shopify-section` wrapper. This element cannot: after the
 *    relocation above it has no such ancestor. Liquid passes
 *    `data-filter-section` instead.
 *
 * 3. APPLY IS ONE ROUND TRIP THAT SWAPS TWO REGIONS. The results wrapper
 *    obviously changes; so does the drawer's own body, because which filter
 *    values still have results depends on the result set. Both are lifted out
 *    of the SAME parsed response, so re-rendering the drawer costs no extra
 *    request. Never the drawer root and never the panel — replacing either
 *    would destroy this instance and restart the slide mid-transition.
 *
 * ---------------------------------------------------------------------------
 * THE OVERLAY PROTOCOL
 * ---------------------------------------------------------------------------
 *
 * Dispatches `filter-drawer:opening` on document BEFORE any state changes, so
 * the mega menu, the mobile menu and the cart drawer can stand down first, then
 * `filter-drawer:toggle` with `{ detail: { open } }` afterwards.
 *
 * NOTE the two are not interchangeable and mixing them throws: `:opening`
 * carries no detail, and predictive-search.js's handleOtherOverlay reads
 * `event.detail.open`. That file therefore registers `filter-drawer:toggle`;
 * everything else registers `filter-drawer:opening`.
 *
 * assets/header.js is deliberately NOT wired to either. The header does not pin
 * itself solid for the cart drawer, on the reasoning that a side-entering
 * drawer never passes under it and pinning would break a transparent header
 * over a hero for no benefit. That applies here unchanged.
 *
 * ---------------------------------------------------------------------------
 * Two things that are deliberate and look like oversights
 * ---------------------------------------------------------------------------
 *
 * 1. EVERY LOOKUP HAPPENS AT EVENT TIME, through getters. Nothing is cached in
 *    connectedCallback. A DOM lookup cached at upgrade time is a bet about
 *    ordering that cannot be won in the theme editor, and it matters twice over
 *    here: [data-filter-body] is replaced on every apply, and the toggle lives
 *    inside [data-filter-results], which is replaced on every apply too.
 *
 * 2. THERE IS NO FOCUS TRAP. Focus is contained by putting `inert` on the two
 *    things behind the drawer (<site-header> and #PageContent), which handles
 *    tab order, the accessibility tree and hit-testing in one attribute with no
 *    bookkeeping to get wrong. Same position the cart drawer takes.
 */

if (!customElements.get('filter-drawer')) {
  customElements.define(
    'filter-drawer',
    class FilterDrawer extends HTMLElement {
      /** The key for themeScroll's counted locks. */
      static LOCK_KEY = 'filter-drawer';

      /** Marks the elements WE made inert, so close() can't clear someone else's. */
      static INERT_FLAG = 'data-filter-inert';

      /** The region of the page the section swaps out when filters are applied. */
      static RESULTS = '[data-filter-results]';

      connectedCallback() {
        // Relocate out of #PageContent before anything else — see note 1 in the
        // file header. appendChild re-fires this callback, so this branch runs
        // once and the second pass does the real setup.
        //
        // Nothing is bound or attached yet at this point, which is what makes
        // the intervening disconnectedCallback a no-op: it guards on
        // `this.ready`, which is not set until init() completes.
        if (this.parentElement !== document.body) {
          const stale = document.getElementById(this.id);
          if (stale && stale !== this) stale.remove();

          document.body.appendChild(this);
          return;
        }

        this.init = this.init.bind(this);

        // Deferred to DOMContentLoaded for two reasons, both of which fail
        // silently otherwise:
        //   1. The toggle lives in the section, which may not be parsed yet.
        //   2. scroll.js is a deferred <head> script, so window.themeScroll
        //      may not exist at upgrade time.
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', this.init, { once: true });
        } else {
          this.init();
        }
      }

      init() {
        this.handleClick = this.handleClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleOtherOverlay = this.handleOtherOverlay.bind(this);
        this.handlePopState = this.handlePopState.bind(this);

        // Fully delegated on document rather than bound to the toggle, the
        // scrim and each control: both [data-filter-body] and the toggle's own
        // wrapper are replaced on every apply, so any listener attached inside
        // either would be discarded by the first successful update.
        document.addEventListener('click', this.handleClick);
        document.addEventListener('keydown', this.handleKeydown);
        document.addEventListener('submit', this.handleSubmit);
        document.addEventListener('cart-drawer:opening', this.handleOtherOverlay);
        document.addEventListener('search:opening', this.handleOtherOverlay);
        window.addEventListener('popstate', this.handlePopState);

        this.decorate();

        this.ready = true;
      }

      disconnectedCallback() {
        document.removeEventListener('DOMContentLoaded', this.init);

        // Guard on a property set during setup, not on a method: methods live
        // on the prototype and are always truthy, so checking one would not
        // tell us whether init() actually ran. It is also what makes the
        // relocation's intervening disconnect a no-op.
        if (!this.ready) return;

        this.close({ silent: true });

        document.removeEventListener('click', this.handleClick);
        document.removeEventListener('keydown', this.handleKeydown);
        document.removeEventListener('submit', this.handleSubmit);
        document.removeEventListener('cart-drawer:opening', this.handleOtherOverlay);
        document.removeEventListener('search:opening', this.handleOtherOverlay);
        window.removeEventListener('popstate', this.handlePopState);

        this.ready = false;
      }

      /**
       * Idempotent, and best-effort by design. If it runs before the section is
       * parsed it simply finds nothing; the drawer still opens, it just
       * announces less well until something calls this again — which the
       * results swap does.
       */
      decorate() {
        const toggle = this.toggle;
        if (!toggle) return;

        toggle.setAttribute('aria-controls', this.id);
        toggle.setAttribute('aria-expanded', this.isOpen ? 'true' : 'false');
      }

      /* ------------------------------------------------------------------ */
      /* Lookups — all at event time, nothing cached                         */
      /* ------------------------------------------------------------------ */

      get toggle() {
        return document.querySelector('[data-filter-toggle]');
      }

      get scrim() {
        return this.querySelector('[data-filter-scrim]');
      }

      get panel() {
        return this.querySelector('[data-filter-panel]');
      }

      get form() {
        return this.querySelector('[data-filter-form]');
      }

      get body() {
        return this.querySelector('[data-filter-body]');
      }

      get status() {
        return this.querySelector('[data-filter-status]');
      }

      get results() {
        return document.querySelector(FilterDrawer.RESULTS);
      }

      get isOpen() {
        return this.hasAttribute('data-open');
      }

      /* ------------------------------------------------------------------ */
      /* Events                                                              */
      /* ------------------------------------------------------------------ */

      handleClick(event) {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;

        const toggle = target.closest('[data-filter-toggle]');
        if (toggle) {
          event.preventDefault();
          if (this.isOpen) {
            this.close();
            this.focusToggle();
          } else {
            this.open();
          }
          return;
        }

        if (!this.isOpen) return;

        if (target.closest('[data-filter-close]')) {
          this.close();
          this.focusToggle();
          return;
        }

        // The scrim is a sibling of the panel, so a click inside the panel
        // never reaches it — no need to check containment the other way.
        if (target.closest('[data-filter-scrim]')) {
          this.close();
          this.focusToggle();
          return;
        }

        const reset = target.closest('[data-filter-reset]');
        if (reset && this.contains(reset)) {
          event.preventDefault();
          this.reset();
        }
      }

      handleKeydown(event) {
        if (event.key !== 'Escape' || !this.isOpen) return;
        this.close();
        this.focusToggle();
      }

      /**
       * Apply. The form is real and its action is the results page, so without
       * this listener — or without JavaScript at all — the same press navigates
       * to the same URL and works.
       */
      handleSubmit(event) {
        const form = event.target;
        if (!form || form !== this.form) return;

        event.preventDefault();
        this.apply(this.urlFromForm(form), { push: true });
      }

      /** Another overlay is taking over. Step aside without a fuss. */
      handleOtherOverlay() {
        this.close({ silent: true });
      }

      /**
       * Back and Forward across applied filters. Re-renders from whatever URL
       * the browser just restored, and must NOT push — the entry already exists.
       */
      handlePopState() {
        this.apply(new URL(window.location.href), { push: false });
      }

      /* ------------------------------------------------------------------ */
      /* Open / close                                                        */
      /* ------------------------------------------------------------------ */

      open() {
        if (this.isOpen) return;

        // Everything else that owns the header or the viewport stands down
        // first, so our toggle below is the last one anything sees.
        document.dispatchEvent(new CustomEvent('filter-drawer:opening'));

        this.setAttribute('data-open', '');
        const scrim = this.scrim;
        if (scrim) scrim.setAttribute('data-open', '');
        const panel = this.panel;
        if (panel) panel.setAttribute('data-open', '');

        this.removeAttribute('inert');
        this.setBackgroundInert(true);

        const toggle = this.toggle;
        if (toggle) toggle.setAttribute('aria-expanded', 'true');

        if (window.themeScroll) window.themeScroll.lock(FilterDrawer.LOCK_KEY);

        this.announce(true);

        // The panel is `invisible` until `data-open` lands, and focus() cannot
        // move into a `visibility: hidden` subtree — it would silently do
        // nothing. Reading a layout property forces the style recalculation
        // first. `visibility` flips at the START of the transition, so by the
        // time this returns the button is genuinely focusable.
        if (panel) void panel.offsetHeight;
        const close = this.querySelector('[data-filter-close]');
        if (close) close.focus();
      }

      close(options) {
        if (!this.isOpen) return;

        const silent = Boolean(options && options.silent);

        this.removeAttribute('data-open');
        const scrim = this.scrim;
        if (scrim) scrim.removeAttribute('data-open');
        const panel = this.panel;
        if (panel) panel.removeAttribute('data-open');

        this.setAttribute('inert', '');

        // MUST come before any focus() call on the toggle: the toggle lives
        // inside #PageContent, and focusing an element that is still inert is a
        // silent no-op that would strand focus on <body>.
        this.setBackgroundInert(false);

        const toggle = this.toggle;
        if (toggle) toggle.setAttribute('aria-expanded', 'false');

        if (window.themeScroll) window.themeScroll.unlock(FilterDrawer.LOCK_KEY);

        if (!silent) this.announce(false);
      }

      focusToggle() {
        const toggle = this.toggle;
        if (toggle) toggle.focus();
      }

      announce(open) {
        this.dispatchEvent(
          new CustomEvent('filter-drawer:toggle', { bubbles: true, detail: { open: open } }),
        );
      }

      /**
       * Focus containment. Flagged with our own attribute so a close can never
       * clear an `inert` something else put there — the cart drawer, the mobile
       * menu and the search panel all manage their own.
       */
      setBackgroundInert(on) {
        const targets = [
          document.querySelector('site-header'),
          document.getElementById('PageContent'),
        ];

        targets.forEach((element) => {
          if (!element) return;

          if (on) {
            if (element.hasAttribute('inert')) return;
            element.setAttribute('inert', '');
            element.setAttribute(FilterDrawer.INERT_FLAG, '');
          } else if (element.hasAttribute(FilterDrawer.INERT_FLAG)) {
            element.removeAttribute('inert');
            element.removeAttribute(FilterDrawer.INERT_FLAG);
          }
        });
      }

      /* ------------------------------------------------------------------ */
      /* Filtering                                                           */
      /* ------------------------------------------------------------------ */

      /**
       * The query string is correct by construction: every control already
       * carries Shopify's own parameter name, so FormData is the URL.
       *
       * EMPTY VALUES ARE DROPPED. Unchecked boxes are absent from FormData
       * already, but a price field left blank is present with an empty value,
       * and `filter.v.price.gte=` is a parameter Shopify has to interpret
       * rather than a filter the shopper set.
       */
      urlFromForm(form) {
        const url = new URL(form.getAttribute('action'), window.location.origin);

        url.search = '';

        new FormData(form).forEach((value, key) => {
          if (typeof value !== 'string' || value === '') return;
          url.searchParams.append(key, value);
        });

        return url;
      }

      /**
       * Clears the drawer's controls, and ONLY the drawer's controls. Nothing
       * is fetched: the shopper presses Apply, exactly as they would after any
       * other change, which is what keeps "nothing happens until Apply" true
       * with no second commit path.
       *
       * NOT form.reset(), which restores each control's INITIAL state — i.e.
       * the filters currently applied to the page, the exact opposite of what
       * this control means. That is also why the button is `type="button"`.
       *
       * Hidden inputs are left alone: `q` and `type` are what make a filtered
       * search a search at all, and clearing them would send the shopper to an
       * empty results page.
       */
      reset() {
        const form = this.form;
        if (!form) return;

        form.querySelectorAll('input[type="checkbox"], input[type="radio"]').forEach((input) => {
          input.checked = false;
        });

        form.querySelectorAll('input[type="number"]').forEach((input) => {
          input.value = '';
        });
      }

      /**
       * One request, two swaps. See note 3 in the file header.
       *
       * `push` is false only for popstate, where the history entry the shopper
       * navigated to already exists and pushing again would trap Back.
       */
      async apply(url, options) {
        if (this.pending) return;

        const sectionId = this.dataset.filterSection;
        if (!sectionId) return;

        const target = new URL(url.toString());
        target.searchParams.set('section_id', sectionId);

        this.setPending(true);

        try {
          const response = await fetch(target.toString());
          if (!response.ok) throw new Error('Section responded ' + response.status);

          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');

          this.replaceResults(parsed);
          this.replaceBody(parsed);

          if (options && options.push) {
            window.history.pushState({}, '', url.toString());
          }

          this.announceStatus(this.dataset.messageApplied);

          // GUARDED, because this method has two callers. After Apply the
          // drawer is open and focus belongs back on the toggle. After popstate
          // it is closed and nothing was pressed, so moving focus would yank a
          // Back navigation somewhere the shopper never asked to go.
          if (this.isOpen) {
            this.close();
            this.focusToggle();
          }
        } catch (error) {
          // Nothing is destroyed: the grid keeps the products it has and every
          // control keeps its state, so the shopper can simply press Apply
          // again. Falling back to a real navigation would be a worse failure —
          // it would discard any pages already loaded by <load-more>.
          this.announceStatus(this.dataset.messageError);
        } finally {
          this.setPending(false);
        }
      }

      /**
       * The results region — heading, the Filter & sort toggle, the grid and
       * the Load more control. All four change with the filters, which is why
       * the wrapper is drawn around the lot rather than around the grid alone.
       *
       * The replacement <load-more> upgrades itself on insertion, and
       * <product-card> is fully delegated, so neither needs re-initialising.
       * decorate() runs afterwards because the toggle inside was replaced with
       * an undecorated copy.
       */
      replaceResults(parsed) {
        const current = this.results;
        const next = parsed.querySelector(FilterDrawer.RESULTS);
        if (!current || !next) throw new Error('results missing');

        current.replaceWith(next);
        this.decorate();
      }

      /**
       * The drawer's own controls. Which filter values still have results
       * depends on the result set, so this has to re-render too — out of the
       * same response, so it costs no extra request.
       *
       * Scroll position is preserved: the shopper's place in a long filter list
       * is theirs, and the panel is mid-close when this runs.
       */
      replaceBody(parsed) {
        const current = this.body;
        const next = parsed.querySelector('[data-filter-body]');
        if (!current || !next) return;

        const region = current.parentElement;
        const scrollTop = region ? region.scrollTop : 0;

        current.replaceWith(next);

        if (region) region.scrollTop = scrollTop;
      }

      /**
       * One in-flight request at a time. Without the guard a double-press sends
       * two applies, and the slower response wins regardless of which set of
       * filters it was built from.
       */
      setPending(on) {
        this.pending = on;

        const body = this.body;
        if (body) {
          if (on) body.setAttribute('aria-busy', 'true');
          else body.removeAttribute('aria-busy');
        }
      }

      announceStatus(message) {
        const status = this.status;
        if (status && message) status.textContent = message;
      }
    },
  );
}
